import api from '../httpClient';

export type SnapshotGoalStatus = 'active' | 'paused' | 'completed' | 'retired';
export type SnapshotGoalType = 'recurring' | 'count' | 'quantity' | 'milestone' | 'deadline' | 'reflective';

export type SnapshotTrackingConfig =
  | { period_unit: 'day' | 'week' | 'month'; target_per_period: number; required_periods: number; consecutive: boolean; week_starts_on: number; unit: string; completion_label: string; first_full_period_start?: string }
  | { target_count: number; unit: string; completion_label: string }
  | { target_value: number | string; unit: string }
  | { milestones: { key?: string; label: string; order?: number }[] }
  | { target_date: string; steps: { key?: string; label: string; order?: number }[] }
  | { prompt: string; target_reflections?: number | null };

export interface SnapshotRadarAxis { key: string; label: string; order: number; latest_value: number | null; initial_value: number | null; last_updated_at: string | null; source_question_count: number }
export interface SnapshotRadar { definition: { key: string; name: string; axis_count: number; scale_min: number; scale_max: number; recent_response_count: number }; is_complete: boolean; axes: SnapshotRadarAxis[] }

export type CountProgress = { kind: 'count'; value: number; target: number; unit: string; completion_label: string; target_reached: boolean };
export type QuantityProgress = { kind: 'quantity'; value: number; target: number; unit: string; target_reached: boolean };
export interface RecurringPeriodProgress { start_date: string; end_date: string; occurred_on: string; value: number; target_ever_reached?: boolean; can_update: boolean; counts_toward_goal: boolean }
export interface RecurringPeriodHistoryItem { position: number; start_date: string; end_date: string; value: number; met_target: boolean; target_ever_reached: boolean; is_current: boolean; is_closed: boolean }
export type RecurringProgress = { kind: 'recurring'; period_unit: 'day' | 'week' | 'month'; unit: string; completion_label: string; current_period_value: number; occurrence_values?: { today: number; yesterday: number }; periods: { current: RecurringPeriodProgress; previous: RecurringPeriodProgress }; period_history?: RecurringPeriodHistoryItem[]; first_full_period_start: string; current_period_is_warm_up: boolean; target_per_period: number; periods_completed: number; qualifying_periods: number; current_streak: number; required_periods: number; consecutive: boolean; target_reached: boolean };
export interface SnapshotProgressStep { key: string; label: string; order: number; completed: boolean }
export type StepProgress = { kind: 'milestone' | 'deadline'; steps: SnapshotProgressStep[]; completed_count: number; target_date?: string; target_reached: boolean };
export type ReflectiveProgress = { kind: 'reflective'; month_count: number; total_count: number; prompt: string; target: number | null; target_reached: boolean };
export type SnapshotGoalProgress = CountProgress | QuantityProgress | RecurringProgress | StepProgress | ReflectiveProgress | { kind: 'unconfigured'; target_reached: false };

export type SnapshotContentKind = 'short_text' | 'long_text' | 'list' | 'labeled_list' | 'challenges' | 'goal_source' | 'accountability';
export interface SnapshotContentItem {
  key: string;
  section: 'where_you_are' | 'where_you_want_to_go' | 'how_you_get_there';
  title: string;
  kind: SnapshotContentKind;
  order: number;
  max_items?: number;
  labels?: string[];
  reflection_format?: 'habit_v1' | 'goal_v1';
  question_exists: boolean;
  question_text: string;
  subtext: string;
  question_type: string | null;
  response_id: number | null;
  value: string | string[];
  display_value: string | string[] | { label: string; value: string }[] | { challenge: string; plan: string; raw: string }[] | { name: string; detail: string; raw: string };
  fingerprint: string;
  is_answered: boolean;
  is_completed: boolean;
  is_draft: boolean;
  updated_at: string | null;
}

export interface SnapshotGoal {
  id: string;
  source_key: string;
  source_question_key?: string | null;
  source_changed?: boolean;
  status: SnapshotGoalStatus;
  title: string;
  motivation: string;
  next_action?: string;
  goal_type: SnapshotGoalType | null;
  tracking_config: SnapshotTrackingConfig | Record<string, never>;
  revision_version: number | null;
  timezone: string;
  start_date: string | null;
  check_in_interval_days: number | null;
  next_check_in_on: string | null;
  last_updated_at: string;
  confirmed_at: string | null;
  progress: SnapshotGoalProgress;
  can_accept_progress: boolean;
  progress_available_on: string | null;
  is_due: boolean;
  checked_in_today: boolean;
  today_check_in_outcome: 'updated' | 'skipped' | 'activated' | null;
}

export interface SnapshotPayload {
  last_updated_at: string;
  season: { label: string; timezone: string };
  plan_changed_since_confirmation: boolean;
  radar: SnapshotRadar | null;
  content: Record<string, SnapshotContentItem>;
  sections: Record<string, string[]>;
  content_progress: { answered: number; total: number };
  goals: SnapshotGoal[];
  due_goal_ids: string[];
}

export type SnapshotProgressUpdate = { idempotency_key: string; occurred_on: string; operation?: 'set' | 'increment' | 'decrement'; value?: number; amount?: number; milestone_key?: string; completed?: boolean; note?: string };
export type SnapshotGoalInput = {
  source_question_key?: string;
  title: string;
  motivation: string;
  next_action?: string;
  goal_type: SnapshotGoalType;
  tracking_config: SnapshotTrackingConfig;
  timezone: string;
  start_date: string;
  check_in_interval_days: number;
  confirm_progress_restart?: boolean;
};
export interface SnapshotGoalProposal { source_question_key: string; source_fingerprint: string; source_value: string[]; proposal: SnapshotGoalInput; ai_generated: boolean }
export type SnapshotDueGoal = SnapshotGoal;
export interface SnapshotDueCheckInPayload { due_count: number; goals: SnapshotDueGoal[] }

const get = async () => (await api.get<SnapshotPayload>('/api/snapshot/')).data;
const saveContent = async (contentKey: string, value: string | string[]) =>
  (await api.put<{ content: SnapshotContentItem }>(`/api/snapshot/content/${contentKey}/`, { value })).data.content;
const suggestGoal = async (sourceQuestionKey: string) =>
  (await api.post<SnapshotGoalProposal>('/api/snapshot/goals/proposal/', { source_question_key: sourceQuestionKey })).data;
const updateGoalProgress = async (goalId: string, update: SnapshotProgressUpdate) =>
  (await api.post<{ goal: SnapshotGoal }>(`/api/snapshot/goals/${goalId}/progress/`, update)).data.goal;
const createGoal = async (input: SnapshotGoalInput) =>
  (await api.post<{ goal: SnapshotGoal }>('/api/snapshot/goals/', input)).data.goal;
const updateGoalPlan = async (goalId: string, input: SnapshotGoalInput) =>
  (await api.put<{ goal: SnapshotGoal }>(`/api/snapshot/goals/${goalId}/plan/`, input)).data.goal;
const updateGoalStatus = async (goalId: string, status: SnapshotGoalStatus) =>
  (await api.patch<{ goal: SnapshotGoal }>(`/api/snapshot/goals/${goalId}/status/`, { status })).data.goal;
const getDueCheckIns = async () => (await api.get<SnapshotDueCheckInPayload>('/api/snapshot/check-ins/due/')).data;
const skipGoalCheckIn = async (goalId: string) =>
  (await api.post<{ goal: SnapshotGoal }>(`/api/snapshot/goals/${goalId}/check-ins/skip/`)).data.goal;

export const snapshot = { get, saveContent, suggestGoal, updateGoalProgress, createGoal, updateGoalPlan, updateGoalStatus, getDueCheckIns, skipGoalCheckIn };
